import {
  MessageSquare,
  Pencil,
  Trash2,
  Play,
  Square as Stop,
  QrCode,
  Star,
  StarOff,
  RotateCw,
} from 'lucide-react';
import type { WhatsAppSession } from '../../types/api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  EmptyState,
  StatusBadge,
} from '../ui';
import { sessionStatusMapping } from './constants';

interface SessionListProps {
  sessions: WhatsAppSession[];
  onEdit: (session: WhatsAppSession) => void;
  onDelete: (session: WhatsAppSession) => void;
  onStart: (session: WhatsAppSession) => void;
  onStop: (session: WhatsAppSession) => void;
  onRestart: (session: WhatsAppSession) => void;
  onShowQrCode: (session: WhatsAppSession) => void;
  onCreateNew: () => void;
}

function normalizeStatus(status: string | undefined): string {
  return (status || '').toLowerCase();
}

function SessionList({
  sessions,
  onEdit,
  onDelete,
  onStart,
  onStop,
  onRestart,
  onShowQrCode,
  onCreateNew,
}: SessionListProps) {
  if (!sessions?.length) {
    return (
      <EmptyState
        title="Nenhuma sessão encontrada"
        description="Crie sua primeira sessão do WhatsApp para começar a automatizar seus atendimentos."
        icon={<MessageSquare className="h-12 w-12" />}
        action={{
          label: 'Criar Sessão',
          onClick: onCreateNew,
        }}
        className="mt-8"
      />
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Nome da Sessão</TableHead>
            <TableHead className="hidden md:table-cell">Assistente</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="hidden md:table-cell">Padrão</TableHead>
            <TableHead className="hidden md:table-cell">Engine</TableHead>
            <TableHead className="relative">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessions.map((session: WhatsAppSession) => {
            const isQrCodeAvailable =
              session.engine === 'GOWS' && normalizeStatus(session.status) === 'scan_qr_code';

            return (
              <TableRow key={session.id}>
                <TableCell>{session.id}</TableCell>
                <TableCell>{session.session_name}</TableCell>
                <TableCell className="hidden md:table-cell">
                  {session.assistant_name || '-'}
                </TableCell>
                <TableCell>
                  <StatusBadge status={session.status} statusMapping={sessionStatusMapping} />
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  {session.isDefault ? (
                    <Star className="h-4 w-4 text-yellow-500" />
                  ) : (
                    <StarOff className="h-4 w-4 text-gray-400" />
                  )}
                </TableCell>
                <TableCell className="hidden md:table-cell">{session.engine || 'GOWS'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    {session.engine === 'GOWS' && (
                      <>
                        <Button
                          variant="ghost"
                          onClick={() => onShowQrCode(session)}
                          disabled={!isQrCodeAvailable}
                          title={
                            !isQrCodeAvailable
                              ? 'QR Code disponível apenas quando aguardando leitura'
                              : 'Obter QR Code'
                          }
                          aria-label="QR Code"
                        >
                          <QrCode className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => onStart(session)}
                          disabled={normalizeStatus(session.status) === 'working'}
                          title={
                            normalizeStatus(session.status) === 'working'
                              ? 'Sessão já está conectada'
                              : 'Iniciar'
                          }
                          aria-label="Iniciar"
                        >
                          <Play className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => onStop(session)}
                          title="Parar"
                          aria-label="Parar"
                        >
                          <Stop className="h-4 w-4 text-red-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => onRestart(session)}
                          title="Reiniciar"
                          aria-label="Reiniciar"
                        >
                          <RotateCw className="h-4 w-4 text-yellow-600" />
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      onClick={() => onEdit(session)}
                      title="Editar"
                      aria-label="Editar"
                    >
                      <Pencil className="h-4 w-4 text-primary-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => onDelete(session)}
                      title="Excluir"
                      aria-label="Excluir"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default SessionList;
